import type { Ics221Checkoff } from 'components/IAP/hooks/ics221EntryHooks';

import { ICS221_CHECKOFF_UNITS } from './documentBoxLabels';

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * A date the 221 fetcher printed, as a `datetime-local` value. The fetcher
 * prints `YYYY-MM-DD HH:mm` for Block 8 and splits Block 3 into `MM/DD/YYYY`
 * and `HH:mm`; anything else reads as blank.
 */
export function printedToLocalInput(value: unknown): string {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(text);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}T${iso[4]}:${iso[5]}`;
  const us = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(text);
  if (us) return `${us[3]}-${us[1]}-${us[2]}T${us[4] ?? '00'}:${us[5] ?? '00'}`;
  return '';
}

/**
 * A `datetime-local` value as the ISO instant the entry stores; blank clears
 * it.
 */
export function localInputToIso(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** An ISO instant as a `datetime-local` value in the viewer's zone. */
export function isoToLocalInput(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export type Ics221CheckoffLine = {
  key: string;
  label: string;
  checkoff: Ics221Checkoff;
  /** Roster name the sheet prints when the line has no signer of its own. */
  defaultName: string;
  /** A line added by hand under Other Staff, which can be removed again. */
  custom: boolean;
};

export type Ics221CheckoffGroup = {
  section: 'Logistics' | 'Finance' | 'Other Staff' | 'Planning';
  lines: Ics221CheckoffLine[];
};

const SECTIONS: readonly {
  section: Ics221CheckoffGroup['section'];
  keys: readonly string[];
}[] = [
  { section: 'Logistics', keys: ['supply', 'communications'] },
  { section: 'Finance', keys: ['time'] },
  { section: 'Other Staff', keys: ['safety', 'agency_rep'] },
  { section: 'Planning', keys: ['documentation', 'demobilization'] },
];

const STANDARD_KEYS = new Set<string>(
  ICS221_CHECKOFF_UNITS.map((unit) => unit.key)
);

const LABELS = new Map<string, string>(
  ICS221_CHECKOFF_UNITS.map((unit) => [unit.key, unit.label])
);

/** The fetcher's roster default for each named unit's signer. */
const LEADER_FIELDS: Record<string, string> = {
  supply: 'supplyUnitName',
  communications: 'communicationsUnitName',
  time: 'timeUnitName',
  documentation: 'documentationUnitName',
  demobilization: 'demobilizationUnitName',
  safety: 'safetyUnitName',
  agency_rep: 'agencyRepName',
};

/**
 * Block 6 laid out as the sheet groups it: Logistics, Finance, Other Staff,
 * Planning. Lines added by hand print under Other Staff, after the named ones.
 */
export function ics221CheckoffGroups(
  inputs: Record<string, unknown>
): Ics221CheckoffGroup[] {
  const checkoffs = (inputs.checkoutCheckoffs ?? {}) as Record<
    string,
    Ics221Checkoff | undefined
  >;
  const line = (key: string, custom: boolean): Ics221CheckoffLine => {
    const checkoff = checkoffs[key] ?? { checked: false };
    const leaderField = LEADER_FIELDS[key];
    return {
      key,
      label: custom ? checkoff.position || key : (LABELS.get(key) ?? key),
      checkoff,
      defaultName:
        leaderField && typeof inputs[leaderField] === 'string'
          ? (inputs[leaderField] as string)
          : '',
      custom,
    };
  };
  const customKeys = Object.keys(checkoffs).filter(
    (key) => !STANDARD_KEYS.has(key) && checkoffs[key]
  );
  return SECTIONS.map(({ section, keys }) => ({
    section,
    lines: [
      ...keys.map((key) => line(key, false)),
      ...(section === 'Other Staff'
        ? customKeys.map((key) => line(key, true))
        : []),
    ],
  }));
}

/** A storage key for an Other Staff line, unique among the lines already there. */
export function customUnitKey(
  label: string,
  existing: Iterable<string>
): string {
  const taken = new Set(existing);
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 80) || 'other_staff';
  if (!taken.has(base) && !STANDARD_KEYS.has(base)) return base;
  let suffix = 2;
  while (taken.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
}

/** The line signed off by the current user, now. */
export function signOffCheckoff(
  checkoff: Ics221Checkoff,
  signer: { name: string; userGuid?: string },
  at: Date = new Date()
): Ics221Checkoff {
  return {
    ...checkoff,
    checked: true,
    name: signer.name || checkoff.name,
    ...(signer.userGuid ? { user_guid: signer.userGuid } : {}),
    datetime: at.toISOString(),
  };
}

/** The line with its sign-off taken back, keeping the requirement and remarks. */
export function clearSignOff(checkoff: Ics221Checkoff): Ics221Checkoff {
  const { datetime: _datetime, user_guid: _userGuid, ...rest } = checkoff;
  return rest;
}
